<template>
	<div>
		<div class="row gutter-sm q-mb-lg">
			<div class="col-xs-12 col-sm-12 q-mt-lg">
				<span class="q-headline text-weight-light">{{ $t('basic_data') }}</span>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-input
					v-model="title"
					:float-label="$t('title')"
					:error="$v.title.$error"
					@input="$v.title.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-input
					v-model="sub_title"
					:float-label="$t('sub_title')"
					:error="$v.sub_title.$error"
					@input="$v.sub_title.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-12">
				<p class="caption">{{ $t('description') }}</p>
				<q-editor
					v-model="description"
					:toolbar="[
						['bold', 'italic', 'strike', 'underline'],
						['left', 'center', 'right', 'justify'],
						['link', 'upload'],
						[
							{
								label: null,
								icon: 'fas fa-font',
								fixedLabel: true,
								fixedIcon: true,
								list: 'no-icons',
								options: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code'],
							},
							{
								label: null,
								icon: 'fas fa-text-height',
								fixedLabel: true,
								fixedIcon: true,
								list: 'no-icons',
								options: [
									'size-1',
									'size-2',
									'size-3',
									'size-4',
									'size-5',
									'size-6',
									'size-7',
								],
							},
							'removeFormat',
						],
						['quote', 'unordered', 'ordered'],
						['custom_btn'],
					]"
					:definitions="{
						bold: { label: null, icon: 'fas fa-bold' },
						italic: { label: null, icon: 'fas fa-italic' },
						strike: { label: null, icon: 'fas fa-strikethrough' },
						underline: { label: null, icon: 'fas fa-underline' },
						left: { label: null, icon: 'fas fa-align-left' },
						center: { label: null, icon: 'fas fa-align-center' },
						right: { label: null, icon: 'fas fa-align-right' },
						justify: { label: null, icon: 'fas fa-align-justify' },
						link: { label: null, icon: 'fas fa-link' },
						upload: { tip: null, icon: 'fas fa-file-image', handler: insertImg },
						removeFormat: { label: null, icon: 'fas fa-eraser' },
						quote: { label: null, icon: 'fas fa-quote-right' },
						unordered: { label: null, icon: 'fas fa-list-ul' },
						ordered: { label: null, icon: 'fas fa-list-ol' },
					}"
				>
					<q-btn
						slot="custom_btn"
						dense
						flat
						color="black"
						icon="fal fa-code"
						label="HTML"
						@click="show_html = true"
					/>
				</q-editor>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-input v-model="campaign_url" :float-label="$t('campaign_url')" />
			</div>

			<div class="col-xs-12 col-sm-6">
				<q-datetime
					v-model="start_date"
					type="date"
					:float-label="$t('start_date')"
					format="DD/MM/YYYY"
					:error="$v.start_date.$error"
					@input="$v.start_date.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-6">
				<q-datetime
					v-model="end_date"
					type="date"
					:float-label="$t('end_date')"
					format="DD/MM/YYYY"
					:error="$v.end_date.$error"
					@input="$v.end_date.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select
					v-model="allow_sells"
					:float-label="$t('allows_invoice_registration')"
					:options="allow_sells_options"
				/>
			</div>

			<div class="col-xs-12 col-sm-3">
				<q-input
					type="number"
					v-model="training_kpi_points"
					:float-label="$t('training_kpi_points')"
					:error="$v.training_kpi_points.$error"
					@input="$v.training_kpi_points.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-3">
				<q-input
					type="number"
					v-model="training_non_kpi_points"
					:float-label="$t('training_non_kpi_points')"
					:error="$v.training_non_kpi_points.$error"
					@input="$v.training_non_kpi_points.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-3">
				<q-input
					type="number"
					v-model="training_special_points"
					:float-label="$t('training_special')"
					:error="$v.training_special_points.$error"
					@input="$v.training_special_points.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-3">
				<q-input
					type="number"
					v-model="webinar_points"
					:float-label="$t('webinar_points')"
					:error="$v.webinar_points.$error"
					@input="$v.webinar_points.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-12">
				<p class="caption">{{ $t('banner_card') }}</p>
				<uploader
					content_type="images"
					@fileUploaded="imageUploaded"
					:multiple="false"
					:cropper="true"
					:error="$v.image.$error"
					@input="$v.image.$touch"
				/>
				<p v-if="image" class="q-mt-md file-url">
					<q-icon name="fal fa-check" color="positive" />
					<a :href="image" target="_blank"> {{ $truncateLink(image) }}</a>
				</p>
			</div>

			<div class="col-xs-12 col-sm-12">
				<p class="caption">{{ $t('banner_topo') }}</p>

				<uploader
					content_type="images"
					@fileUploaded="imageDesktopUploaded"
					:multiple="false"
					:cropper="true"
					:aspect_ratio="192 / 40"
					:aspect_ratio_string="'192/40'"
					:error="$v.image_banner.$error"
					@input="$v.image_banner.$touch"
				/>

				<p v-if="image_banner" class="q-mt-md file-url">
					<q-icon name="fal fa-check" color="positive" />
					<a :href="image_banner" target="_blank"> {{ $truncateLink(image_banner) }}</a>
				</p>
			</div>
		</div>

		<q-tabs v-model="tab" color="secondary" class="shadow-1">
			<q-tab
				slot="title"
				name="permissions"
				icon="fal fa-clipboard-list-check"
				:label="$t('permissions')"
			/>

			<q-tab-pane name="permissions" class="q-px-lg q-pb-xl">
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-12">
						<q-select
							v-model="area"
							:options="areas"
							:float-label="$t('areas')"
							:error="$v.area.$error"
							@input="$v.area.$touch"
							multiple
							chips
						/>
					</div>

					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('profiles') }}</p>

						<div class="row gutter-sm">
							<div class="col-xs-12 col-sm-12">
								<q-checkbox
									v-model="all_profiles"
									:val="true"
									:label="$t('all')"
									@input="checkAll()"
									:disable="disableData"
								/>
							</div>

							<div
								class="col-xs-12 col-sm-4"
								v-for="(item, index) in profiles"
								:key="index"
								:class="getErrorClass($v.profile.$error)"
							>
								<q-checkbox
									keep-color
									v-model="profile"
									:val="item.value"
									:label="item.label"
									@input="$v.profile.$touch"
									:color="getErrorColor($v.profile.$error)"
								/>
							</div>
						</div>
					</div>
				</div>
			</q-tab-pane>
		</q-tabs>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="far fa-chevron-left"
					@click="$router.push({ name: 'campaigns' })"
				/>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
				<q-btn
					color="positive"
					class="full-width"
					:label="$t('save')"
					icon="far fa-save"
					@click="saveData()"
					:disable="disableWhenFinished"
				/>
			</div>
		</div>
		<q-modal
			v-model="show_html"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
			no-backdrop-dismiss
			no-esc-dismiss
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="show_html = false" icon="fas fa-times" />
					<q-toolbar-title>
						HTML
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row">
						<div class="col-12">
							<q-input
								type="textarea"
								rows="10"
								float-label="HTML"
								v-model="description"
							/>
						</div>
					</div>

					<div class="row gutter-sm justify-end q-mt-lg">
						<div class="col-xs-12 col-sm-3">
							<q-btn
								color="tertiary"
								class="full-width"
								:label="$t('close')"
								icon="far fa-ban"
								@click="show_html = false"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import Uploader from 'components/Uploader'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { date } from 'quasar'
const { formatDate } = date
import * as moment from 'moment'
import * as tz from 'moment-timezone'
import { BlobServiceClient } from '@azure/storage-blob'

export default {
	name: 'Basic-Data',

	components: {
		uploader: Uploader,
	},

	data() {
		return {
			tab: 'permissions',
			all_profiles: false,
			allow_sells_options: [
				{
					label: this.$t('allow'),
					value: true,
				},
				{
					label: this.$t('not_allow'),
					value: false,
				},
			],
			upload_url: process.env.API_URL + '/files/policy/',
			urls: [],
			cancel_token: '',
			source: '',
			crop_modal: false,
			original_image: '',
			fileUrl: '',
			show_html: false,
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			areas: 'registration_lists/country_areas',
			profiles: 'registration_lists/profiles',
			id: 'campaigns/id',
			vtitle: 'campaigns/title',
			vsub_title: 'campaigns/sub_title',
			vdescription: 'campaigns/description',
			vcampaign_url: 'campaigns/campaign_url',
			vimage: 'campaigns/image',
			vallow_sells: 'campaigns/allow_sells',
			vtraining_kpi_points: 'campaigns/training_kpi_points',
			vtraining_non_kpi_points: 'campaigns/training_non_kpi_points',
			vtraining_special_points: 'campaigns/training_special_points',
			vwebinar_points: 'campaigns/webinar_points',
			varea: 'campaigns/areas',
			vprofile: 'campaigns/profiles',
			vstart_date: 'campaigns/start_date',
			vend_date: 'campaigns/end_date',
			status: 'campaigns/status',
			vimage_banner: 'campaigns/image_banner',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},

		title: {
			get() {
				return this.vtitle
			},
			set(val) {
				this.$store.dispatch('campaigns/SET_TITLE', [val])
			},
		},

		sub_title: {
			get() {
				return this.vsub_title
			},
			set(val) {
				this.$store.dispatch('campaigns/SET_SUBTITLE', [val])
			},
		},

		description: {
			get() {
				return this.vdescription
			},
			set(val) {
				this.$store.dispatch('campaigns/SET_DESCRIPTION', [val])
			},
		},

		campaign_url: {
			get() {
				return this.vcampaign_url
			},
			set(val) {
				this.$store.dispatch('campaigns/SET_URL', [val])
			},
		},

		start_date: {
			get() {
				return moment(this.vstart_date).format('YYYY/MM/DD')
			},
			set(val) {
				this.$store.dispatch('campaigns/SET_STARTDATE', [
					moment(val).format('YYYY-MM-DDT12:00:00'),
				])
			},
		},

		end_date: {
			get() {
				return moment(this.vend_date).format('YYYY/MM/DD')
			},
			set(val) {
				this.$store.dispatch('campaigns/SET_ENDDATE', [
					moment(val).format('YYYY-MM-DDT12:00:00'),
				])
			},
		},

		image: {
			get() {
				return this.vimage
			},
			set(val) {
				this.$store.dispatch('campaigns/SET_IMAGE', [val])
			},
		},

		allow_sells: {
			get() {
				return this.vallow_sells
			},
			set(val) {
				this.$store.dispatch('campaigns/SET_ALLOW_SELLS', [val])
			},
		},

		training_kpi_points: {
			get() {
				return this.vtraining_kpi_points
			},
			set(val) {
				this.$store.dispatch('campaigns/SET_TRAINING_KPI_POINTS', [val])
			},
		},

		training_non_kpi_points: {
			get() {
				return this.vtraining_non_kpi_points
			},
			set(val) {
				this.$store.dispatch('campaigns/SET_TRAINING_NON_KPI_POINTS', [val])
			},
		},

		training_special_points: {
			get() {
				return this.vtraining_special_points
			},
			set(val) {
				this.$store.dispatch('campaigns/SET_TRAINING_SPECIAL_POINTS', [val])
			},
		},

		webinar_points: {
			get() {
				return this.vwebinar_points
			},
			set(val) {
				this.$store.dispatch('campaigns/SET_WEBINAR_POINTS', [val])
			},
		},

		area: {
			get() {
				return this.varea
			},
			set(val) {
				this.$store.dispatch('campaigns/SET_AREAS', [val])
			},
		},

		profile: {
			get() {
				return this.vprofile
			},
			set(val) {
				this.$store.dispatch('campaigns/SET_PROFILES', [val])
			},
		},

		image_banner: {
			get() {
				return this.vimage_banner
			},
			set(val) {
				this.$store.dispatch('campaigns/SET_IMAGE_BANNER', [val])
			},
		},

		disableData() {
			if (this.status == 1 || this.status == 4) return true
			else return false
		},

		disableWhenFinished() {
			if (this.status == 4) return true
			else return false
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '80vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},
	},

	methods: {
		saveData() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()
				let data = {
					title: this.title,
					sub_title: this.sub_title,
					description: this.description,
					campaign_url: this.campaign_url,
					start_date: moment(this.vstart_date).format('YYYY-MM-DD'),
					end_date: moment(this.vend_date).format('YYYY-MM-DD'),
					image: this.image,
					image_banner: this.image_banner,
					areas: this.area,
					profiles: this.profile,
					allow_sells: this.allow_sells,
					training_kpi_points: this.training_kpi_points,
					training_non_kpi_points: this.training_non_kpi_points,
					training_special_points: this.training_special_points,
					webinar_points: this.webinar_points,
				}

				if (this.id != 0) {
					this.$axios
						.put(`/admin_rewards/campaigns/${this.id}/`, data, this.getHeader)
						.then((response) => {
							this.saveSuccess()
							this.saveDataVuex(response)
							this.$store.dispatch('campaigns/SET_ID', [response.data.id])
						})
						.catch((e) => {
							console.log(e)
							this.saveError()
						})
						.finally(() => {
							this.$q.loading.hide()
						})
				} else {
					this.$axios
						.post(`/admin_rewards/campaigns/`, data, this.getHeader)
						.then((response) => {
							this.saveSuccess()
							this.saveDataVuex(response)
							this.$store.dispatch('campaigns/SET_ID', [response.data.id])

							this.$router.replace({
								name: 'campaign',
								params: { id: response.data.id },
							})
						})
						.catch((e) => {
							console.log(e)
							this.saveError()
						})
						.finally(() => {
							this.$q.loading.hide()
						})
				}
			} else {
				this.$q.notify({
					message: this.$t('make_sure_that_all_fields_are_filled_in'),
					type: 'negative',
					icon: 'fal fa-exclamation-triangle',
					position: 'top',
				})
			}
		},

		imageUploaded(url) {
			this.image = url
		},

		imageDesktopUploaded(url) {
			this.image_banner = url
		},

		checkAll() {
			if (this.all_profiles) {
				this.profiles.forEach((element) => {
					this.profile.push(element.value)
				})
			} else {
				this.profile = []
			}
		},

		getErrorColor(state) {
			if (state) return 'negative'
			else return ''
		},

		getErrorClass(state) {
			if (state) return 'checkbox-error'
			else return ''
		},

		getOrder(name) {
			if (name == 'cancel') {
				if (this.$q.platform.is.mobile) return 'order-last'
				else return 'order-first'
			} else if (name == 'save') {
				if (this.$q.platform.is.mobile) return 'order-first'
				else return 'order-last'
			}
		},

		saveDataVuex(response) {
			this.$store.dispatch('campaigns/SET_CAMPAIGN', [
				response.data.title,
				response.data.sub_title,
				response.data.description,
				response.data.campaign_url,
				moment.utc(response.data.start_date).format('YYYY-MM-DD'),
				moment.utc(response.data.end_date).format('YYYY-MM-DD'),
				response.data.image,
				response.data.status,
				response.data.allow_sells,
				response.data.training_kpi_points,
				response.data.training_non_kpi_points,
				response.data.training_special_points,
				response.data.webinar_points,
				response.data.image_banner,
				response.data.areas,
				response.data.profiles,
				response.data.company_branches,
				response.data.brands,
				response.data.product_types,
				response.data.products,
				response.data.rewards,
				response.data.upload_file_types,
			])
		},

		saveSuccess() {
			this.$q.notify({
				message: this.$t('basic_data_save_success'),
				type: 'positive',
				icon: 'fal fa-save',
			})
		},

		saveError() {
			this.$q.notify({
				message: this.$t('basic_data_save_error'),
				type: 'negative',
				icon: 'fal fa-ban',
			})
		},

		insertImg() {
			const input = document.createElement('input')
			input.type = 'file'
			input.accept = '.png, .jpg, .gif'
			let file

			input.onchange = (_) => {
				const files = Array.from(input.files)
				file = files[0]

				this.uploadFile(file)
			}
			input.click()
		},

		uploadFile(file) {
			let date = moment()

			let fd = new FormData()
			fd.append('filename', file.name)
			fd.append(
				'file_directory',
				`media/html_images/campaigns/${date.year()}/${date.month() + 1}/`
			)

			this.$axios
				.post('/api/files/policy/', fd, this.getHeader, { cancelToken: this.source.token })
				.then((response) => {
					return new Promise((resolve) => {
						this.$q.loading.show()

						if (response.data.origin === 'azure') {
							const blobServiceClient = new BlobServiceClient(
								`${response.data.base_url}?${response.data.sas_token}`
							)

							const main = async () => {
								const container_client = blobServiceClient.getContainerClient('')

								const block_blob_client = container_client.getBlockBlobClient(
									response.data.filename
								)
								const upload_blob_response = await block_blob_client.uploadBrowserData(
									file
								)

								let fd_complete = new FormData()
								fd_complete.append('uploaded', true)
								fd_complete.append('fileSize', file.size)
								fd_complete.append('file', response.data.file_id)

								if (this.custom_directory) {
									fd_complete.append('not_signed', true)
									fd_complete.append('bucket_name', 'static')
								}

								this.$axios
									.post('/api/files/complete/', fd_complete, this.getHeader)
									.then((response) => {
										this.fileUrl = response.data.fileUrl
										document.execCommand(
											'insertHTML',
											true,
											'<div><img class="uploadBase" src="' +
												this.fileUrl +
												'" style="max-width: 100%" /></div>'
										)
									})
									.finally(() => {
										resolve()
										this.$q.loading.hide()
									})
							}

							main()
						} else {
							let content_type =
								file.type != '' ? file.type : 'application/octet-stream'
							let policy = response.data
							let file_name = policy.filename
							let key_path = policy.file_bucket_path

							let fd_s3 = new FormData()
							fd_s3.append('key', key_path + file_name)
							fd_s3.append('acl', 'private')
							fd_s3.append('Content-Type', content_type)
							fd_s3.append('AWSAccessKeyId', policy.key)
							fd_s3.append('Policy', policy.policy)
							fd_s3.append('filename', file_name)
							fd_s3.append('Signature', policy.signature)
							fd_s3.append('file', file)

							this.cancel_token = this.$axios.CancelToken
							this.source = this.cancel_token.source()

							this.$axios
								.post(process.env.BUCKET_URL, fd_s3, {
									// onUploadProgress: function( event ) {
									//     updateProgress(event.loaded / event.total)
									// },
									// cancelToken: this.source.token
								})
								.then(() => {
									let fd_complete = new FormData()
									fd_complete.append('uploaded', true)
									fd_complete.append('fileSize', file.size)
									fd_complete.append('file', policy.file_id)

									this.$axios
										.post('/api/files/complete/', fd_complete, this.getHeader)
										.then((response) => {
											let data = {
												file_url: response.data.fileUrl,
											}

											this.$axios
												.post('/utils/presigned_url/', data, this.getHeader)
												.then((response) => {
													document.execCommand(
														'insertHTML',
														true,
														`<div><img class="uploadBase" src="${
															response.data.file_url
														}" style="max-width: 100%" /></div>`
													)
												})
												.catch((e) => {
													console.log(e)
												})
												.finally(() => {
													this.$q.loading.hide()
												})
										})
										.finally(() => {
											this.$q.loading.hide()
										})
								})
								.finally(() => {
									resolve()
								})
						}
					})
				})
				.catch((e) => {
					console.log(e)
					this.$q.loading.hide()
				})
		},
	},

	validations() {
		return {
			title: { required },
			sub_title: { required },
			description: { required },
			area: { required },
			profile: { required },
			start_date: { required },
			end_date: { required },
			training_kpi_points: { required },
			training_non_kpi_points: { required },
			training_special_points: { required },
			webinar_points: { required },
			image: { required },
			image_banner: { required },
		}
	},
}
</script>

<style></style>
